import { createAsyncThunk } from '@reduxjs/toolkit'
import { RootState } from '../store'
import { BASE_URL } from '../config'
import { getIDToken } from '../utils/AuthUtil'
import { setStructuralSettings, showLoader } from '../actions/structural'
import { StructuralSettings } from '../models/Structural'
import { ConvertData } from '../models/Construction'
import { SelectionManager } from '../visualization/selection/selectionManager'
import { SceneManager } from '../visualization/sceneManager'
import { EventType } from '../utils/EventEmitter'

export const fetchAllObjectSettings = createAsyncThunk(
  'structuralSetting/fetchAllObjectSettings',
  async (payload: { file_id: number }, { dispatch, getState }) => {
    const state = getState() as RootState
    try {
      const { file_id } = payload
      await fetch(`${BASE_URL}/api/structuralSettings/findAll`, {
        method: 'POST',
        headers: {
          'Access-Control-Allow-Origin': '*',
          Authorization: `Bearer ${await getIDToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          filterBy: [{ name: 'file_id', value: file_id }],
        }),
      })
        .then((response) => response.json())
        .then((data) => {
          dispatch(setStructuralSettings(data))
          dispatch(showLoader(false))
        })
    } catch (error: any) {
      if (error.name !== 'AbortError') {
        console.error(
          `Error during fetching structural settings: ${error.message}`,
          error,
        )
      } else {
        throw error
      }
    }
  },
)

export const updateObjectSettings = createAsyncThunk(
  'structuralSetting/updateObjectSettings',
  async (
    payload: { settings: StructuralSettings[] },
    { dispatch, getState },
  ) => {
    const state = getState() as RootState
    try {
      await fetch(`${BASE_URL}/api/structuralSettings/createOrUpdate`, {
        method: 'POST',
        headers: {
          'Access-Control-Allow-Origin': '*',
          Authorization: `Bearer ${await getIDToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload.settings),
      })
        .then((response) => response.json())
        .then((data) => {
          dispatch(
            fetchAllObjectSettings({ file_id: payload.settings[0]?.file_id! }),
          )
          dispatch(showLoader(false))
        })
    } catch (error: any) {
      if (error.name !== 'AbortError') {
        console.error(
          `Error during fetching structural settings: ${error.message}`,
          error,
        )
      } else {
        throw error
      }
    }
  },
)

export const rerunStructuralAutomation = createAsyncThunk(
  'structuralSetting/rerunStructuralAutomation',
  async (_, { dispatch, getState }) => {
    const state = getState() as RootState
    const selectedFile = state.organization.selectedFile
    const s3Path = selectedFile?.path || ''
    const s3PathParts = s3Path.split('/')
    const s3Folder = s3PathParts.slice(0, -1).join('/')
    const s3Filename = s3PathParts[s3PathParts.length - 1]
    const selectionManager = SelectionManager.getInstance()
    selectionManager.deselect()
    const eventEmitter = SceneManager.getInstance().getEventEmitter()

    try {
      dispatch(showLoader(true))
      const response = await fetch(`${BASE_URL}/api/structural`, {
        method: 'POST',
        headers: {
          'Access-Control-Allow-Origin': '*',
          Authorization: `Bearer ${await getIDToken()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          s3_bucketname: process.env.REACT_APP_S3_BUCKET_NAME,
          s3_folder: s3Folder,
          s3_file: s3Filename,
        }),
      })
      const convertData: ConvertData = await response.json()
      eventEmitter?.emit(EventType.StructuralAutomationFinished, {
        building: convertData.building,
      })
      dispatch(showLoader(false))
    } catch (error: any) {
      if (error.name !== 'AbortError') {
        console.error(
          `Error during rerunning structural automation: ${error.message}`,
          error,
        )
      } else {
        throw error
      }
    }
  },
)
